import { listedOnTools } from './outreach-alfred';

/**
 * The Connectors genome — pure geometry + path helpers for the DESKTOP-WEB hero.
 *
 * DESKTOP-WEB is a horizontal reflow of the same diagram (Figma 14033-151437): the green Symphony
 * app-icon hub sits in the CENTRE between two dashed **circles**, the channel badges run down the LEFT
 * and the tool badges down the RIGHT, each wired to the hub by a smooth S-curve, plus one "+N" overflow
 * chip below each column. The signal flows left → right (channels INTO the hub, tools OUT of it).
 *
 * Coordinates match the Figma frame (1280×372) so the desktop card reads the same as the design. The
 * geometry is authored here (not a native export). Mobile-web
 * (`connectors/mobile/ConnectorsGenomeMobile.tsx`) is a SEPARATE presentation that carries its own
 * vertical native-export copy and is deliberately left untouched — only the desktop hero
 * (`ConnectorsGenome.tsx`) reads from here.
 */

/* The horizontal design box. Element sizes are the Figma-absolute values (icon buttons 42px, hub 77px,
   12px labels, rings r152/r108 — node 14033-151615); the FRAME width is sized to the real card (~960,
   not the Figma mock's 1280) so the SVG renders ≈1:1 instead of shrinking every circle and label to
   ~74%. The horizontal margins are the only thing trimmed — all sizes and vertical positions are the
   design's. */
export const GENOME_VW = 960;
export const GENOME_VH = 372;
/* The rendered viewBox crops the frame so the content sits with ~35px of clearance above the top label
   and below the "+N" chip (Figma 14033-151615) — the card frames the diagram tightly at the design's
   element sizes. */
export const GENOME_VIEWBOX = '0 4 960 370';
export const GENOME_HUB = { x: 480, y: 186 };
/* The green app-icon hub (Figma `LOGO` ≈ 77px). */
export const GENOME_MARK_SIZE = 77;
/* Icon-button plate radius (diameter 42 — the Figma `.Icon Button`). */
export const GENOME_BADGE_R = 21;
/* Where a wire ends at the hub: a small circle set INSIDE the app-icon tile (half-width 38.5), so every
   wire tucks under the green logo — the tile is painted over the spokes, so each line reads as starting
   flush from the logo edge with no gap (the middle wires exposed a gap when this sat outside the tile). */
export const GENOME_HUB_EDGE_R = 30;

/* Motion (mirrors native ConnectorsHeroView): the badges fly out of the hub (staggered spring), then —
   once the whole entrance has settled (ARRIVAL_MS) — a dot runs each wire forever. */
export const GENOME_ARRIVAL_MS = 1250;
export const GENOME_PULSE_STAGGER_MS = 240;

/* Channels folded into the "+N": supported (WhatsApp, Claude, Claude Code, Gemini, Apps & Web, Wearables)
   minus the three shown as their own badge — mirrors native's `supportedChannelCount − shownChannelCount`.
   This counts CHANNELS, not route slugs: `ai-coders` contributes both Claude and Claude Code, so the two
   numbers move independently of `CHANNEL_SLUGS.length` and adding a group means editing this by hand. */
export const GENOME_SHOWN_CHANNELS = 3;
export const GENOME_SUPPORTED_CHANNELS = 6;
export const GENOME_SHOWN_TOOLS = 3;

/**
 * The tools "+N": every connector the Tools tab lists, minus the three shown as their own badge. A row the
 * Tools tab leaves out (`listedOnTools`: a server-hidden row, or Alfred while it is on Outreach) is not
 * counted, so the "+N" never advertises a tool the overflow tap cannot land on. Mirrors native
 * `ConnectorsHeroModel.toolOverflowCount`.
 */
export function genomeToolOverflowCount(
  connectors: readonly { toolkit: string; hiddenFromConnectorsList?: boolean }[],
  alfredOnOutreach: boolean,
): number {
  return Math.max(0, connectors.filter((c) => listedOnTools(c, alfredOnOutreach)).length - GENOME_SHOWN_TOOLS);
}

/* Two dashed rings — TRUE circles, concentric on the hub (Figma 14033-151615: outer r≈152, inner r≈108). */
export const GENOME_RINGS = [{ r: 152 }, { r: 108 }];

export type GenomeSide = 'left' | 'right';

/* Each column carries a gentle arc: the top badge and the "+N" chip sit further OUT (toward the frame
   edge) than the two middle badges — mirroring the Figma placement. Index 0-2 are the badge slots (top,
   middle, bottom); index 3 is the overflow chip. Left and right are mirror images about the hub. Values
   are the Figma x's re-centred on the trimmed 960 frame (each shifted 158 toward the hub). */
const LEFT_X = [202, 224, 224, 202];
const RIGHT_X = [758, 736, 736, 758];
const ROW_Y = [78, 160, 248];
const OVERFLOW_Y = 318;

/** A badge/chip centre: `left`/`right` column, `slot` 0-2 for a badge, or `overflow` for the chip. */
export function genomeCentre(side: GenomeSide, slot: number, overflow: boolean) {
  const xs = side === 'left' ? LEFT_X : RIGHT_X;
  return {
    x: xs[overflow ? 3 : slot],
    y: overflow ? OVERFLOW_Y : ROW_Y[slot],
  };
}

/**
 * The wire from a badge to the hub: a flow-chart S-curve with horizontal tangents at both ends, ending
 * on a small circle around the hub at the badge's own angle (so the wires spread rather than converge).
 * Returned as `{ d, end }` — `end` is the point the travelling dot resolves to at the hub.
 */
export function genomeSpokePath(centre: { x: number; y: number }): { d: string; end: { x: number; y: number } } {
  const dx = centre.x - GENOME_HUB.x;
  const dy = centre.y - GENOME_HUB.y;
  const len = Math.hypot(dx, dy) || 1;
  const end = {
    x: GENOME_HUB.x + (dx / len) * GENOME_HUB_EDGE_R,
    y: GENOME_HUB.y + (dy / len) * GENOME_HUB_EDGE_R,
  };
  const midX = (centre.x + end.x) / 2;
  return { d: `M${centre.x},${centre.y} C${midX},${centre.y} ${midX},${end.y} ${end.x},${end.y}`, end };
}
